import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  alpha,
  useTheme,
  Divider,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Category as CategoryIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";

/**
 * Dialog for selecting which category-related input items are effective materials
 * for a category output item.
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - onConfirm: function(selectedImportIds: number[])
 * - categoryName: string
 * - categoryInputItems: array of input item rows with same fromCategoryId
 * - selectedImportIds: pre-selected importIds (from effectiveMaterialsMap)
 * - nonCategoryInputCount: number of non-category input items (always included)
 */
function EffectiveMaterialsDialog({
  open,
  onClose,
  onConfirm,
  categoryName,
  categoryInputItems,
  selectedImportIds,
  nonCategoryInputCount,
}) {
  const theme = useTheme();
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) {
      setSelected(selectedImportIds || []);
    }
  }, [open, selectedImportIds]);

  const handleToggle = (importId) => {
    setSelected((prev) =>
      prev.includes(importId)
        ? prev.filter((id) => id !== importId)
        : [...prev, importId]
    );
  };

  const hasItems = categoryInputItems && categoryInputItems.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CategoryIcon color="primary" />
            <Typography variant="h6">Select Effective Materials</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Category: <strong>{categoryName}</strong>
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {!hasItems ? (
          <Alert severity="error">
            No category-related input items found in this order. You must add
            at least one category input item before using{" "}
            <strong>{categoryName}</strong> as an output category.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which category input items are effective materials for{" "}
              <strong>{categoryName}</strong> output. At least one must be
              selected.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              {categoryInputItems.map((item) => (
                <FormControlLabel
                  key={item.importId}
                  control={
                    <Checkbox
                      checked={selected.includes(item.importId)}
                      onChange={() => handleToggle(item.importId)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ py: 0.25 }}>
                      <Typography variant="body2" fontWeight="600">
                        {item.rawItemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.brand} · Qty: {parseFloat(item.qty || 0).toFixed(2)}{" "}
                        {item.unitType}
                      </Typography>
                    </Box>
                  }
                  sx={{ display: "flex", mb: 0.5, alignItems: "flex-start" }}
                />
              ))}
            </Paper>

            {nonCategoryInputCount > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <InfoIcon sx={{ fontSize: 16, color: "info.main" }} />
                <Typography variant="caption" color="info.main">
                  <strong>{nonCategoryInputCount}</strong> non-category input
                  item(s) are always included in the material cost calculation.
                </Typography>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {hasItems && (
          <Button
            onClick={() => onConfirm(selected)}
            variant="contained"
            disabled={selected.length === 0}
          >
            Confirm ({selected.length} selected)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default EffectiveMaterialsDialog;
